import { isDefined } from "@clipboard-health/util-ts";
import { CHAT_PAGE_FULL_PATH } from "@src/appV2/Chat/paths";
import { useGetCredentialSharingConfiguration } from "@src/appV2/Facilities/api/useGetCredentialSharingConfiguration";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { UpdateChartingCredentialsContainer } from "@src/appV2/redesign/Charting/UpdateChartingCredentialsContainer";
import { YourChartingLoginBottomSheetContainer } from "@src/appV2/redesign/Charting/YourChartingLoginBottomSheetContainer";
import { useCreateChatChannel } from "@src/appV2/redesign/ShiftDiscovery/Blocks/api/useCreateChatChannel";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { type Shift } from "@src/appV2/Shifts/Shift/types";
import { useGetChartingCredentials } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { generatePath, useHistory } from "react-router-dom";

import { ChartingRequiredCardContent } from "./ChartingRequiredCardContent";

export interface ChartingRequiredCardProps {
  shift: Pick<Shift, "_id" | "facility">;
  workerId: string;
}

export function ChartingRequiredCard(props: ChartingRequiredCardProps) {
  const { shift, workerId } = props;

  const saveLoginModalState = useModalState();
  const yourLoginModalState = useModalState();
  const history = useHistory();

  const { mutateAsync: createChatChannel } = useCreateChatChannel();

  const { credentialSharingConfiguration } = useGetCredentialSharingConfiguration(
    shift.facility.userId ?? "",
    {
      enabled: isDefined(shift.facility.userId),
    }
  );

  const { data: chartingCredentialsResponse, isLoading: isLoadingCredentials } =
    useGetChartingCredentials(workerId, shift.facility.userId ?? "", {
      enabled: isDefined(shift.facility.userId),
    });

  const chartingCredentials =
    chartingCredentialsResponse?.data?.[0]?.attributes?.chartingCredentials;

  const handleRequestLogin = async () => {
    logEvent(APP_V2_USER_EVENTS.CHARTING_REQUIRED_CARD_REQUEST_LOGIN, {
      shiftId: shift._id,
      workplaceId: shift.facility.userId ?? "",
      workerId,
    });

    try {
      await createChatChannel({
        facilityId: shift.facility.userId ?? "",
        agentId: workerId,
      });

      const prefilledMessage =
        "Hello, could you please share my username and password for charting? Thank you!";
      const pathname = generatePath(CHAT_PAGE_FULL_PATH, {
        facilityId: shift.facility.userId ?? "",
      });
      const params = new URLSearchParams({ prefilledMessage });
      history.push({
        pathname,
        search: `?${params.toString()}`,
      });
    } catch (error) {
      logEvent(APP_V2_USER_EVENTS.CHARTING_REQUIRED_CARD_CHAT_CHANNEL_ERROR, {
        error: error instanceof Error ? error.message : String(error),
        workerId,
        workplaceId: shift.facility.userId ?? "",
        shiftId: shift._id,
      });
    }
  };

  const handleSaveLogin = () => {
    logEvent(APP_V2_USER_EVENTS.CHARTING_REQUIRED_CARD_SAVE_LOGIN, {
      shiftId: shift._id,
      workplaceId: shift.facility.userId ?? "",
      workerId,
    });
    saveLoginModalState.openModal();
  };

  const handleMyChartingLogin = () => {
    logEvent(APP_V2_USER_EVENTS.CHARTING_REQUIRED_CARD_MY_LOGIN, {
      shiftId: shift._id,
      workplaceId: shift.facility.userId ?? "",
      workerId,
    });
    yourLoginModalState.openModal();
  };

  // Don't render if feature is not enabled
  if (!credentialSharingConfiguration?.chartingReminders?.bookedShiftDetails.enabled) {
    return null;
  }

  return (
    <>
      <ChartingRequiredCardContent
        chartingCredentials={chartingCredentials}
        onRequestLogin={handleRequestLogin}
        onSaveLogin={handleSaveLogin}
        onMyChartingLogin={handleMyChartingLogin}
      />

      <UpdateChartingCredentialsContainer
        modalState={saveLoginModalState}
        shiftId={shift._id}
        workplaceId={shift.facility.userId ?? ""}
        workerId={workerId}
        chartingCredentials={chartingCredentials}
        onClose={() => {
          saveLoginModalState.closeModal();
        }}
      />

      <YourChartingLoginBottomSheetContainer
        modalState={yourLoginModalState}
        shiftId={shift._id}
        workplaceId={shift.facility.userId ?? ""}
        workerId={workerId}
        chartingCredentials={chartingCredentials}
        isLoadingCredentials={isLoadingCredentials}
      />
    </>
  );
}
